import { useState, useEffect } from 'react';
import { X, Copy, Check, Download, QrCode, Loader2, AlertCircle, FileText } from 'lucide-react';
import api from '../../services/api';

interface WireGuardPeer {
  id: string;
  name: string;
  assignedIp: string;
  allowedIps: string[];
  persistentKeepalive: number;
}

interface WireGuardExportProps {
  isOpen: boolean;
  onClose: () => void;
  edgeId: string;
  peer: WireGuardPeer | null;
}

interface ConfigData {
  config: string;
  instructions: string[];
}

const WireGuardExport = ({ isOpen, onClose, edgeId, peer }: WireGuardExportProps) => {
  const [configData, setConfigData] = useState<ConfigData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [showQR, setShowQR] = useState(false);

  useEffect(() => {
    if (isOpen && peer) {
      fetchConfig();
    } else {
      setConfigData(null);
      setError(null);
      setShowQR(false);
    }
  }, [isOpen, peer]);

  const fetchConfig = async () => {
    if (!peer) return;

    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/api/v2/edges/${edgeId}/wireguard/peers/${peer.id}/config`);
      setConfigData(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load configuration');
    } finally {
      setLoading(false);
    }
  };

  const copyConfig = async () => {
    if (configData?.config) {
      await navigator.clipboard.writeText(configData.config);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const downloadConfig = () => {
    if (!configData?.config || !peer) return;

    const blob = new Blob([configData.config], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `wg-${peer.name.toLowerCase().replace(/\s+/g, '-')}.conf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Simple QR code generation using a canvas
  // For production, you'd want to use a library like 'qrcode'
  const generateQRCode = (text: string): string => {
    // This is a placeholder - in production, use a proper QR library
    // For now, we'll use a public QR code API
    const encoded = encodeURIComponent(text);
    return `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encoded}`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <FileText className="w-5 h-5 text-blue-600" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Export Configuration</h3>
              <p className="text-sm text-gray-500">{peer?.name}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
              <span className="ml-2 text-gray-600">Loading configuration...</span>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center space-x-2">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
              <span className="text-sm text-red-700">{error}</span>
            </div>
          )}

          {configData && !loading && (
            <>
              {/* Instructions */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-blue-900 mb-2">Setup Instructions</h4>
                <ol className="list-decimal list-inside space-y-1 text-sm text-blue-800">
                  {configData.instructions.map((instruction, idx) => (
                    <li key={idx}>{instruction}</li>
                  ))}
                </ol>
              </div>

              {/* View Toggle */}
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setShowQR(false)}
                  className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                    !showQR
                      ? 'bg-blue-100 text-blue-700 font-medium'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <FileText className="w-4 h-4 inline mr-1" />
                  Config File
                </button>
                <button
                  onClick={() => setShowQR(true)}
                  className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                    showQR
                      ? 'bg-blue-100 text-blue-700 font-medium'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <QrCode className="w-4 h-4 inline mr-1" />
                  QR Code
                </button>
              </div>

              {/* Config Text */}
              {!showQR && (
                <div className="space-y-3">
                  <div className="relative">
                    <pre className="bg-gray-900 text-gray-100 rounded-lg p-4 text-sm font-mono overflow-x-auto">
                      {configData.config}
                    </pre>
                    <div className="absolute top-2 right-2 flex items-center space-x-1">
                      <button
                        onClick={copyConfig}
                        className="bg-gray-700 hover:bg-gray-600 text-white px-2 py-1 rounded text-xs inline-flex items-center space-x-1"
                        title="Copy to clipboard"
                      >
                        {copied ? (
                          <>
                            <Check className="w-3 h-3" />
                            <span>Copied!</span>
                          </>
                        ) : (
                          <>
                            <Copy className="w-3 h-3" />
                            <span>Copy</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    <button
                      onClick={downloadConfig}
                      className="btn-secondary inline-flex items-center space-x-2 px-4 py-2"
                    >
                      <Download className="w-4 h-4" />
                      <span>Download .conf</span>
                    </button>
                    <button
                      onClick={copyConfig}
                      className="btn-secondary inline-flex items-center space-x-2 px-4 py-2"
                    >
                      {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      <span>{copied ? 'Copied!' : 'Copy Config'}</span>
                    </button>
                  </div>
                </div>
              )}

              {/* QR Code */}
              {showQR && (
                <div className="text-center space-y-4">
                  <div className="inline-block bg-white p-4 rounded-lg border border-gray-200">
                    <img
                      src={generateQRCode(configData.config)}
                      alt="WireGuard Configuration QR Code"
                      className="w-64 h-64 mx-auto"
                    />
                  </div>
                  <p className="text-sm text-gray-600">
                    Scan this QR code with your IoT gateway's WireGuard app to import the configuration.
                  </p>
                  <p className="text-xs text-amber-600">
                    Note: You'll still need to add your private key and PSK after scanning.
                  </p>
                </div>
              )}

              {/* GL.iNet Opal Specific Instructions */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-gray-900 mb-2">GL.iNet Opal Setup</h4>
                <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
                  <li>
                    Access your Opal's admin panel at{' '}
                    <code className="bg-gray-200 px-1 rounded">192.168.8.1</code>
                  </li>
                  <li>
                    Navigate to <strong>VPN → WireGuard Client</strong>
                  </li>
                  <li>
                    Click <strong>Add a New Group</strong> then{' '}
                    <strong>Add a New Configuration</strong>
                  </li>
                  <li>Choose "Input Manually" and paste the configuration above</li>
                  <li>
                    The Opal will generate a keypair - copy the <strong>Public Key</strong> and enter
                    it when adding this peer in VSM
                  </li>
                  <li>
                    Enter the <strong>PresharedKey</strong> that was shown when you created this peer
                  </li>
                  <li>
                    Click <strong>Apply</strong> to save and connect
                  </li>
                </ol>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
          <button onClick={onClose} className="btn-secondary px-4 py-2">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default WireGuardExport;
